import { ApiHandlerDiscreteParameter, createHandlerChainRunner } from "@insite/client-framework/HandlerCreator";
import {
    UpdateCartApiParameter,
    updateCartLineWithResult,
} from "@insite/client-framework/Services/CartService";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCart";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";

interface LineWhiteGloveSelection {
    cartLineId: string;
    isWhiteGlove: boolean;
}

type HandlerType = ApiHandlerDiscreteParameter<
    {
        selections: LineWhiteGloveSelection[];
    },
    UpdateCartApiParameter,
    never,
    { cartId?: string; changedLines?: CartLineModel[] }
>;

/**
 * Resolves the active checkout cart and determines which lines actually changed
 * their per-line `isWhiteGlove` flag, so only those lines are patched.
 */
export const PopulateChangedLines: HandlerType = props => {
    const state = props.getState();
    const {
        pages: {
            checkoutReviewAndSubmit: { cartId },
        },
    } = state;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    if (!cart || !cart.cartLines) {
        throw new Error("There was no current cart while updating per-line white glove selections.");
    }

    props.cartId = cartId;

    const selectionById = new Map(props.parameter.selections.map(s => [s.cartLineId, s.isWhiteGlove]));
    const changedLines: CartLineModel[] = [];
    cart.cartLines.forEach(cartLine => {
        const desired = selectionById.get(cartLine.id) === true;
        const current = cartLine.properties?.isWhiteGlove === "true";
        if (desired !== current) {
                    const { isMadeToOrder, ...properties } = cartLine.properties ?? {};
                    changedLines.push({
                        ...cartLine,
                        properties: {
                            ...properties,
                            isWhiteGlove: String(desired),
                        },
                    });
                }
    });

    props.changedLines = changedLines;
};

/**
 * Persists the per-line white glove flag directly via the cart line PATCH endpoint
 * (which reliably runs CopyCustomPropertiesToEntity). Runs sequentially and does NOT
 * reload the cart per call, so the shipping pipeline is recalculated only once (LoadCart step).
 */
export const UpdateChangedLines: HandlerType = async props => {
    const cartId = props.cartId ?? "current";
    for (const cartLine of props.changedLines ?? []) {
        await updateCartLineWithResult({ cartId, cartLine });
    }
};

export const LoadCart: HandlerType = props => {
    const state = props.getState();
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    if (cartId) {
        props.dispatch(loadCart({ cartId, getPromotions: true }));
    } else {
        props.dispatch(loadCurrentCart({ getPromotions: true }));
    }
};

export const chain = [PopulateChangedLines, UpdateChangedLines, LoadCart];

const updateLinesWhiteGlove = createHandlerChainRunner(chain, "updateLinesWhiteGlove");
export default updateLinesWhiteGlove;
